import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL, LOGO_URL } from "../../../config";
import { Loader2, Mail, Lock, CheckCircle, ArrowLeft, Key } from "lucide-react";
import { ForgotPasswordModalProps } from "../../../types/auth";

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ 
    isOpen, 
    onClose,
    onSwitchToLogin
}) => {
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    if (!isOpen) return null;

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        try {
            await axios.post(`${API_BASE_URL}/user/forgot-password`, { emailId: email });
            setStep(2);
        } catch (err: any) {
            setError(err.response?.data?.message || "User not found or error sending OTP");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");
        try {
            await axios.post(`${API_BASE_URL}/user/forgot-password/verify-otp`, { emailId: email, otp });
            setStep(3);
        } catch (err: any) {
            setError(err.response?.data?.message || "Invalid or expired OTP");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        setIsLoading(true);
        setError("");
        try {
            await axios.post(`${API_BASE_URL}/user/reset-password`, { 
                emailId: email, 
                otp, 
                newPassword: password 
            });
            setSuccess("Password reset successfully! Redirecting...");
            setTimeout(() => {
                onClose();
                onSwitchToLogin();
                // Reset state
                setStep(1);
                setEmail("");
                setOtp("");
                setPassword("");
                setConfirmPassword("");
                setSuccess("");
            }, 2000);
        } catch (err: any) {
            setError(err.response?.data?.message || "Error resetting password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-8 animate-in fade-in zoom-in duration-300 space-y-6">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors"
                >
                    ✕
                </button>

                <div className="text-center">
                    <img
                        src={LOGO_URL}
                        alt="Company Logo"
                        className="mx-auto h-12 w-auto mb-6"
                    />
                    <h2 className="text-2xl font-bold text-gray-900">
                        {step === 1 && "Forgot Password?"}
                        {step === 2 && "Verify OTP"}
                        {step === 3 && "Reset Password"}
                    </h2>
                    <p className="mt-2 text-sm text-gray-500">
                        {step === 1 && "Enter your username email to receive a password reset OTP"}
                        {step === 2 && "Enter the 6-digit code sent to your email"}
                        {step === 3 && "Create a new strong password for your account"}
                    </p>
                </div>

                {error && (
                    <div className="p-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-200 flex items-start space-x-3">
                         <div className="flex-shrink-0 mt-0.5">
                            <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-sm mb-1">Error</h3>
                            <p className="text-xs leading-relaxed">{error}</p>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="p-4 text-sm text-green-700 bg-green-50 rounded-lg border border-green-200 flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <CheckCircle className="size-5 text-[#2563eb]" />
                        </div>
                        <div>
                            <h3 className="font-bold text-sm mb-1">Success</h3>
                            <p className="text-xs leading-relaxed">{success}</p>
                        </div>
                    </div>
                )}

                {step === 1 && (
                    <form onSubmit={handleSendOtp} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <div className="relative group">
                                <Mail className="absolute left-3 top-3 size-5 text-gray-400 group-focus-within:text-[#2563eb] transition-colors" />
                                <input
                                    type="text"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 bg-[#f8f9fa] border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] transition-all"
                                    placeholder="Enter your username/email"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-2.5 bg-[#2563eb] hover:bg-[#2d8e47] text-white font-bold rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isLoading ? <Loader2 className="size-5 animate-spin" /> : "Send OTP"}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={handleVerifyOtp} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 text-center font-bold">Enter OTP</label>
                            <div className="relative group text-center">
                                <input
                                    type="text"
                                    required
                                    maxLength={6}
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    className="w-full bg-[#f8f9fa] border-b-2 border-gray-200 py-3 text-center text-3xl font-bold tracking-[0.5em] focus:border-[#2563eb] outline-none transition-all"
                                    placeholder="000000"
                                />
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                            >
                                <ArrowLeft className="size-4" /> Back
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex-[2] py-2.5 bg-[#2563eb] hover:bg-[#2d8e47] text-white font-bold rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                            >
                                {isLoading ? <Loader2 className="size-5 animate-spin" /> : "Verify OTP"}
                            </button>
                        </div>
                    </form>
                )}

                {step === 3 && (
                    <form onSubmit={handleResetPassword} className="space-y-5">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                                <div className="relative group">
                                    <Lock className="absolute left-3 top-3 size-5 text-gray-400 group-focus-within:text-[#2563eb] transition-colors" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        minLength={6}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-10 pr-10 py-2.5 bg-[#f8f9fa] border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] transition-all"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <Key className="size-5" /> : <Lock className="size-5" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                                <div className="relative group">
                                    <Lock className="absolute left-3 top-3 size-5 text-gray-400 group-focus-within:text-[#2563eb] transition-colors" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        minLength={6}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-10 pr-10 py-2.5 bg-[#f8f9fa] border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#2563eb] focus:border-[#2563eb] transition-all"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setStep(2)}
                                className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                            >
                                <ArrowLeft className="size-4" /> Back
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex-[2] py-2.5 bg-gray-900 hover:bg-black text-white font-bold rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                            >
                                {isLoading ? <Loader2 className="size-5 animate-spin" /> : "Reset Password"}
                            </button>
                        </div>
                    </form>
                )}

                <div className="text-center mt-6 py-2">
                    <button
                        type="button"
                        onClick={onSwitchToLogin}
                        className="font-semibold text-gray-900 hover:text-[#2563eb] transition-colors text-sm"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordModal;
